import { calendarDay } from '@/shared/lib/due-date'
import type { IsoDate, ProjectSummary } from '@/shared/types/domain'

/**
 * LA MATEMÁTICA DE LA LÍNEA DE TIEMPO VIVE ACÁ, SEPARADA DEL DIBUJO.
 *
 * No es prolijidad: es lo único que hace testeable esta pantalla. `jsdom` no
 * tiene layout, así que un test unitario no puede medir dónde quedó una barra
 * —`getBoundingClientRect` devuelve ceros— ni cuánto mide. Si la posición se
 * calculara mientras se dibuja, la única forma de verificarla sería un e2e por
 * cada caso de borde, y los casos de borde acá son muchos: un proyecto sin
 * fecha de inicio, uno sin entrega, uno que empieza antes de la ventana, uno
 * que termina después, uno de un solo día.
 *
 * Con las fechas convertidas a porcentajes por funciones puras, el componente
 * sólo escribe `left` y `width`, y todos esos bordes se afirman en milisegundos.
 */

/** Cuántos días cubre cada ventana. Son los tres cortes que se ofrecen. */
export const WINDOWS = {
  trimestre: 90,
  semestre: 180,
  anio: 365,
} as const

export type TimelineWindow = keyof typeof WINDOWS

export interface TimelineDomain {
  /** Primer día visible, inclusive. */
  from: IsoDate
  /** Último día visible, inclusive. */
  to: IsoDate
  /** Cuántos días cubre. Nunca cero: evita dividir por cero en `barMetrics`. */
  days: number
  /** Hoy como porcentaje del ancho, o `null` si hoy cae fuera de la ventana. */
  todayPct: number | null
}

/** Días calendario entre dos ISO, sin husos ni horas: `to - from`. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  const a = Date.UTC(
    Number(from.slice(0, 4)),
    Number(from.slice(5, 7)) - 1,
    Number(from.slice(8, 10)),
  )
  const b = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)))
  return Math.round((b - a) / 86_400_000)
}

/** Suma días a un ISO y devuelve otro ISO. */
export function addDays(date: IsoDate, days: number): IsoDate {
  const at = new Date(
    Date.UTC(
      Number(date.slice(0, 4)),
      Number(date.slice(5, 7)) - 1,
      Number(date.slice(8, 10)) + days,
    ),
  )
  return at.toISOString().slice(0, 10)
}

/**
 * La ventana visible.
 *
 * Arranca **un mes antes de hoy**, no en hoy: lo que venció la semana pasada es
 * justamente lo que hay que mirar, y una línea de tiempo que empieza hoy lo
 * deja afuera. El resto de la ventana va hacia adelante.
 */
export function timelineDomain(
  window: TimelineWindow,
  today: IsoDate = calendarDay(),
): TimelineDomain {
  const days = WINDOWS[window]
  const from = addDays(today, -30)
  const to = addDays(from, days)
  const offset = daysBetween(from, today)

  return {
    from,
    to,
    days,
    todayPct: offset >= 0 && offset <= days ? (offset / days) * 100 : null,
  }
}

/** Por qué un proyecto no tiene barra. Se muestra: no se esconde el proyecto. */
export type MissingDates = 'sin-inicio' | 'sin-entrega' | 'sin-fechas'

export type BarState = 'done' | 'overdue' | 'active' | 'upcoming'

export interface Bar {
  leftPct: number
  widthPct: number
  /** La barra se sale por la izquierda de la ventana (empezó antes). */
  clippedStart: boolean
  /** Se sale por la derecha (termina después). */
  clippedEnd: boolean
  state: BarState
}

/**
 * En qué estado se dibuja la barra.
 *
 * `done` gana sobre todo lo demás: un proyecto culminado no está vencido aunque
 * su fecha objetivo haya pasado — se entregó tarde, que es historia, no alarma.
 */
export function barState(project: ProjectSummary, today: IsoDate): BarState {
  if (project.stage.category === 'done') return 'done'
  if (project.target_end_date && daysBetween(project.target_end_date, today) > 0) return 'overdue'
  if (project.start_date && daysBetween(project.start_date, today) >= 0) return 'active'
  return 'upcoming'
}

/**
 * Qué le falta a un proyecto para poder dibujarse. `null` = no le falta nada.
 *
 * **Una fecha que falta no se inventa.** Un proyecto sin `target_end_date` no es
 * un proyecto que termina hoy, y uno sin `start_date` no es uno que empezó el
 * lunes: son datos que nadie cargó. Rellenarlos dibujaría una barra que afirma
 * algo que el sistema no sabe, y esa barra se vería exactamente igual que una
 * cierta.
 */
export function missingDates(project: ProjectSummary): MissingDates | null {
  const { start_date: inicio, target_end_date: fin } = project
  if (!inicio && !fin) return 'sin-fechas'
  if (!inicio) return 'sin-inicio'
  if (!fin) return 'sin-entrega'
  return null
}

/**
 * La barra de un proyecto dentro de la ventana, en porcentajes.
 *
 * Devuelve `null` si el proyecto no tiene las dos fechas, o si su rango no toca
 * la ventana. Lo que sí toca se **recorta** a los bordes y se marca con
 * `clippedStart` / `clippedEnd`, para que el dibujo pueda decir "esto sigue" en
 * vez de fingir que el proyecto empieza donde empieza la pantalla.
 */
export function barMetrics(
  project: ProjectSummary,
  domain: TimelineDomain,
  today: IsoDate = calendarDay(),
): Bar | null {
  const { start_date: inicio, target_end_date: fin } = project
  if (!inicio || !fin) return null

  // Un proyecto que termina antes de empezar es un dato mal cargado, no un
  // proyecto de ancho negativo: se dibuja como un solo día.
  const desde = daysBetween(domain.from, inicio)
  const hasta = Math.max(desde, daysBetween(domain.from, fin))

  if (hasta < 0 || desde > domain.days) return null

  const recortadoDesde = Math.max(0, desde)
  const recortadoHasta = Math.min(domain.days, hasta)

  return {
    leftPct: (recortadoDesde / domain.days) * 100,
    // Mínimo de medio día de ancho: un proyecto de un día tiene que verse.
    widthPct: Math.max(((recortadoHasta - recortadoDesde) / domain.days) * 100, 0.5),
    clippedStart: desde < 0,
    clippedEnd: hasta > domain.days,
    state: barState(project, today),
  }
}

export type GroupBy = 'empresa' | 'responsable' | 'ninguno'

export interface TimelineGroup {
  key: string
  label: string
  /** El hue de la espina, sólo cuando el grupo ES una empresa. */
  hue: number | null
  projects: ProjectSummary[]
}

/**
 * Agrupa para dibujar carriles.
 *
 * `ninguno` devuelve un solo grupo con todo: es la vista plana, el portafolio
 * entero sobre un eje. Los tres cortes contestan preguntas distintas —"¿cómo
 * viene cada empresa?", "¿cómo viene cada persona?", "¿cómo viene todo?"— sobre
 * exactamente los mismos proyectos.
 */
export function groupProjects(
  projects: readonly ProjectSummary[],
  by: GroupBy,
  sinResponsable: string,
  todos: string,
): TimelineGroup[] {
  if (by === 'ninguno') {
    return [{ key: 'todos', label: todos, hue: null, projects: [...projects] }]
  }

  const grupos = new Map<string, TimelineGroup>()
  for (const project of projects) {
    const key =
      by === 'empresa' ? project.company.id : (project.internal_owner?.id ?? 'sin-responsable')
    const label =
      by === 'empresa'
        ? project.company.name
        : (project.internal_owner?.display_name ?? sinResponsable)
    const hue = by === 'empresa' ? project.company.color_hue : null

    const grupo = grupos.get(key) ?? { key, label, hue, projects: [] }
    grupo.projects.push(project)
    grupos.set(key, grupo)
  }

  // Alfabético, y "Sin responsable" siempre último: es la ausencia de un
  // grupo, no un grupo más.
  return [...grupos.values()].sort((a, b) => {
    if (a.key === 'sin-responsable') return 1
    if (b.key === 'sin-responsable') return -1
    return a.label.localeCompare(b.label)
  })
}

/** Los cortes de mes de la ventana, para las guías verticales y el eje. */
export function monthTicks(domain: TimelineDomain): { date: IsoDate; leftPct: number }[] {
  const ticks: { date: IsoDate; leftPct: number }[] = []
  let year = Number(domain.from.slice(0, 4))
  let month = Number(domain.from.slice(5, 7))

  // Se arranca en el primer día del mes SIGUIENTE al del borde izquierdo: el
  // borde ya es una línea, y un tick pegado a él se lee como ruido.
  for (;;) {
    month += 1
    if (month > 12) {
      month = 1
      year += 1
    }
    const date = `${String(year)}-${String(month).padStart(2, '0')}-01`
    const offset = daysBetween(domain.from, date)
    if (offset > domain.days) break
    ticks.push({ date, leftPct: (offset / domain.days) * 100 })
  }
  return ticks
}
